import { Info, ShieldCheck, Gauge, BarChart3 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface Props {
  /** Optional label to show next to the info icon. */
  label?: string;
  /** Visual size of the trigger. */
  size?: "sm" | "md";
  className?: string;
}

/**
 * Reusable explainer for AleaEdge's Edge % methodology.
 * Mobile-friendly (tap-to-open Popover). Does not change any math —
 * purely educational/transparency UI.
 */
export default function EdgeExplainer({ label = "How Edge is calculated", size = "sm", className = "" }: Props) {
  const iconSize = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 text-[11px] md:text-xs text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline ${className}`}
          aria-label="How AleaEdge calculates Edge percentage"
        >
          <Info className={iconSize} />
          <span>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[min(92vw,360px)] p-0 overflow-hidden border-border/70"
      >
        <div className="bg-gradient-card p-4 border-b border-border/50">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-7 h-7 rounded-lg bg-primary/15 text-primary">
              <ShieldCheck className="w-4 h-4" />
            </span>
            <div className="font-display font-bold text-sm">How AleaEdge measures Edge</div>
          </div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Edge % compares our projected probability against the sportsbook's implied probability
            on the best available price across major US books.
          </p>
        </div>
        <ul className="p-4 space-y-2.5 text-xs leading-relaxed">
          <li className="flex gap-2">
            <Gauge className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Conservative by design.</strong> Built on real
              sportsbook pricing discrepancies and AI-assisted market analysis — not inflated
              projections.
            </span>
          </li>
          <li className="flex gap-2">
            <BarChart3 className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Real market data.</strong> Consensus probability
              is averaged across multiple sportsbooks before we calculate edge.
            </span>
          </li>
          <li className="flex gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
            <span className="text-muted-foreground">
              <strong className="text-foreground">Long-term transparency.</strong> Many betting
              apps inflate edge percentages to look impressive. AleaEdge prioritizes durable,
              honest signal over hype.
            </span>
          </li>
        </ul>
        <div className="px-4 pb-4 -mt-1">
          <p className="text-[11px] text-muted-foreground/80 italic">
            "We prioritize long-term transparency over inflated projections."
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Derive supporting confidence/signal labels from a pick's existing
 * confidence_score + (optional) edge value. Pure presentation — does
 * NOT change the underlying edge math.
 */
export type SignalGrade = {
  confidence: { label: string; tone: string };
  marketSignal: { label: string; tone: string };
};

export function deriveSignalGrade(confidence?: number | null, edgePct?: number | null): SignalGrade {
  const c = Number(confidence ?? 0);
  const e = Number(edgePct ?? 0);

  const confidence_ =
    c >= 85
      ? { label: "PREMIUM GRADE", tone: "border-accent/40 bg-accent/10 text-accent" }
      : c >= 75
      ? { label: "HIGH CONFIDENCE", tone: "border-success/40 bg-success/10 text-success" }
      : c >= 60
      ? { label: "SOLID READ", tone: "border-primary/40 bg-primary/10 text-primary" }
      : { label: "LEAN", tone: "border-muted-foreground/30 bg-muted/30 text-muted-foreground" };

  const marketSignal =
    e >= 4
      ? { label: "SHARP MOVEMENT DETECTED", tone: "border-accent/40 bg-accent/10 text-accent" }
      : e >= 2
      ? { label: "STRONG MARKET SIGNAL", tone: "border-success/40 bg-success/10 text-success" }
      : e >= 1
      ? { label: "ELEVATED DISAGREEMENT", tone: "border-primary/40 bg-primary/10 text-primary" }
      : e > 0
      ? { label: "MARKET EDGE", tone: "border-warning/40 bg-warning/10 text-warning" }
      : { label: "NEUTRAL MARKET", tone: "border-muted-foreground/30 bg-muted/30 text-muted-foreground" };

  return { confidence: confidence_, marketSignal };
}
